import { gql, TypedDocumentNode } from '@apollo/client';
import type {
  CancelAppointmentMutation,
  CancelAppointmentMutationVariables,
  CreateConsultMutation,
  CreateConsultMutationVariables,
  CreateOnDemandConsultMutation,
  CreateOnDemandConsultMutationVariables,
  GetAppointmentsQuery,
  GetAppointmentsQueryVariables,
  GetCompletedConsultsQuery,
  GetCompletedConsultsQueryVariables,
  GetConsultQuery,
  GetConsultQueryVariables,
  GetConsultReportQuery,
  GetConsultReportQueryVariables,
  GetConsultsQuery,
  GetConsultsQueryVariables,
  GetOrdersQuery,
  GetOrdersQueryVariables,
  GetOrdersUPPQuery,
  GetOrdersUPPQueryVariables,
  OnDemandAvailableQuery,
  OnDemandAvailableQueryVariables,
  UpdateConsultMutation,
  UpdateConsultMutationVariables,
} from 'gql/graphql';

export const CONSULTS = gql`
  query GetConsults {
    consults {
      __typename
      ... on ConsultsListSuccess {
        consultList {
          id
          onDemand
          created
          reportedOn
          report
          # modified
          phone
          status
          topic
          bookedSlot
          email
          orderrequestExternalId
          participants {
            firstName
          }
          queueDescription {
            aheadCount
          }
          # queuePosition
          counselor {
            bio
            # bookedSlots
            firstName
            id
            lastName
            photo
          }
        }
      }
    }
  }
` as TypedDocumentNode<GetConsultsQuery, GetConsultsQueryVariables>;

export const GET_CONSULT = gql`
  query GetConsult($id: String) {
    consults(id: $id) {
      __typename
      ... on ConsultsListSuccess {
        consultList {
          id
          onDemand
          created
          # modified
          phone
          status
          state
          topic
          bookedSlot
          counselor {
            bio
            # bookedSlots
            firstName
            id
            lastName
            photo
          }
        }
      }
    }
  }
` as TypedDocumentNode<GetConsultQuery, GetConsultQueryVariables>;

export const APPOINTMENTS = gql`
  query GetAppointments($state: String!, $topic: String!) {
    appointments(state: $state, topic: $topic) {
      __typename
      ... on AppointmentSuccess {
        freeSlots {
          slotDate
          slotHours {
            slotHour
            slotCounselors {
              counselorId
            }
          }
        }
        foundFreeSlots
        counselors {
          counselorFullname
          counselorId
        }
      }
    }
  }
` as TypedDocumentNode<GetAppointmentsQuery, GetAppointmentsQueryVariables>;

export const UPDATE_CONSULT = gql`
  mutation UpdateConsult($id: String!, $input: ConsultUpdateInput) {
    updateConsult(id: $id, input: $input) {
      __typename
      ... on UpdateConsultSuccess {
        consult {
          consultId
        }
      }
    }
  }
` as TypedDocumentNode<UpdateConsultMutation, UpdateConsultMutationVariables>;

export const CREATE_CONSULT = gql`
  mutation CreateConsult($input: ConsultInput) {
    createConsult(input: $input) {
      __typename
      ... on CreateConsultSuccess {
        consult {
          consultId
        }
      }
    }
  }
` as TypedDocumentNode<CreateConsultMutation, CreateConsultMutationVariables>;

export const ON_DEMAND_AVAILABLE = gql`
  query OnDemandAvailable($topic: String!) {
    onDemandAvailable(topic: $topic) {
      __typename
      ... on FieldInvalid {
        message
        fields
        status
      }
      ... on OnDemandAvailSuccess {
        available
      }
    }
  }
` as TypedDocumentNode<OnDemandAvailableQuery, OnDemandAvailableQueryVariables>;

export const CREATE_ON_DEMAND_CONSULT = gql`
  mutation CreateOnDemandConsult($input: ConsultInput) {
    createConsult(input: $input) {
      __typename
      ... on CreateConsultSuccess {
        consult {
          consultId
        }
      }
    }
  }
` as TypedDocumentNode<
  CreateOnDemandConsultMutation,
  CreateOnDemandConsultMutationVariables
>;

export const CANCEL_CONSULT = gql`
  mutation CancelAppointment($id: String!) {
    cancelConsult(id: $id) {
      __typename
    }
  }
` as TypedDocumentNode<
  CancelAppointmentMutation,
  CancelAppointmentMutationVariables
>;

export const GET_ORDERS = gql`
  query GetOrders {
    __typename
    orders {
      __typename
      ... on OrdersListSuccess {
        ordersList {
          customerprofileId
        }
      }
    }
  }
` as TypedDocumentNode<GetOrdersQuery, GetOrdersQueryVariables>;

export const GET_ORDERS_UPP = gql`
  query GetOrdersUPP($myriskOnly: Boolean!) {
    __typename
    orders(myriskOnly: $myriskOnly) {
      __typename
      ... on OrdersListSuccess {
        ordersList {
          product
          productId
          patientFirstName
          createDate
          hasReleasedResults
          resultsReleasedToPatient
          accessionId
          customerprofileId
          externalId
        }
      }
    }
  }
` as TypedDocumentNode<GetOrdersUPPQuery, GetOrdersUPPQueryVariables>;

export const GET_CONSULT_REPORT = gql`
  query GetConsultReport($consultId: String!) {
    consultReport(id: $consultId) {
      __typename
      ... on ReportSuccess {
        report
        filename
        contentType
      }
    }
  }
` as TypedDocumentNode<GetConsultReportQuery, GetConsultReportQueryVariables>;

export const GET_COMPLETED_CONSULTS = gql`
  query GetCompletedConsults {
    consults {
      __typename
      ... on ConsultsListSuccess {
        consultList {
          id
          counseleeId
          bookedSlot
          terminated
          reportedOn
          topic
          report
          counselor {
            firstName
            lastName
          }
          participants {
            firstName
            lastName
          }
          status
        }
      }
    }
  }
` as TypedDocumentNode<
  GetCompletedConsultsQuery,
  GetCompletedConsultsQueryVariables
>;
